/**
 * `Run billing (dry)` — `mockups/studio/money.html`'s topbar, second button.
 *
 * `billing_run$` with `dry_run: true` computes a whole month for one client and rolls
 * it back, answering with the invoice it would have written, line by line. That is
 * what this renders: pick a client, see the lines, and nothing is committed —
 * `DryRunInvoiceSchema.ref` is **always null**, because a reference minted inside a
 * transaction that is rolled back would be a gap in a sequence Wise matching needs to
 * be gapless.
 *
 * `.docs/20` §Edge cases: "Dry run on a client with nothing to bill → 'nothing to
 * bill' line, no dialog error." `would_issue: false` is that case, and it arrives as a
 * **success** — a zero-line month is an answer, not a refusal, and rendering it as one
 * is the whole point of the edge case.
 *
 * It is still a `$` action, so it still confirms. The confirm says the period and the
 * client and says that it commits nothing, which is the honest version of "name the
 * amount": before the run there is no amount to name.
 *
 * **Three answers, not one**, and session 24 is why. The dialog used to promise "what
 * it shows is what the real run would write" flatly, and for the month on screen —
 * the one the period seg opens on, which is the month that has *already* been billed
 * — that was $41.40 short of the invoice sitting two sections below it on the same
 * page. A promise a page cannot keep about money is worse than no preview at all,
 * because the preview is what somebody reads before letting the real run go. So the
 * answer now says which of three cases it is, and `run.ts` `dryView` is where each is
 * decided:
 *
 *   - `already_issued` — the period has an invoice. A real run is a no-op on the
 *     UNIQUE `(client_id, period)`, and what is shown is that invoice's own lines.
 *   - `would_issue` — the period is open and the arrears month has ended. Exact.
 *   - `arrears_in_progress` — the arrears month is still running, so the usage line
 *     is read as far as today and will be larger by the 1st. Said, not hidden.
 */

import { useState } from 'react'

import type { DryRunInvoiceView } from '@fareast/shared'

import { messageOf } from '../../../api/problem.js'
import { reviewOf, useStudioAction } from '../../../api/studio.js'
import { NeedsReviewNotice } from '../../../components/studio/NeedsReviewNotice.js'
import { Button, ButtonRow } from '../../../components/portal/Button.js'
import { Empty } from '../../../components/portal/Disclosure.js'
import { Stack } from '../../../components/portal/Layout.js'
import { Dialog } from '../../../components/portal/Overlay.js'
import { Notice } from '../../../components/portal/Status.js'
import { TBody, THead, Table, Td, Th, Tr } from '../../../components/portal/Table.js'
import { B, Muted, Num, Prose, Small } from '../../../components/portal/Text.js'
import { useFormat } from '../../../format/useFormat.js'
import type { NameOf } from './names.js'

export type DryRunDialogProps = {
  readonly period: string
  /** Who can be run. The run table's clients, plus anybody the book knows. */
  readonly clients: readonly string[]
  readonly nameOf: NameOf
}

export function DryRunDialog({ period, clients, nameOf }: DryRunDialogProps) {
  const format = useFormat()
  const [open, setOpen] = useState(false)
  const [chosen, setChosen] = useState<string | null>(null)

  const run = useStudioAction('billing_run')
  const review = reviewOf(run.data)
  const preview = previewOf(run.data)

  return (
    <>
      <Button
        size="sm"
        onClick={() => {
          setOpen(true)
        }}
      >
        Run billing (dry)
      </Button>

      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next)
          if (!next) {
            setChosen(null)
            run.reset()
          }
        }}
        title={`What ${format.monthLong(period)} would bill`}
      >
        <Stack>
          <Prose>
            A dry run does the whole month and rolls it back, so what it shows is what the real run
            would write. It mints no reference and commits nothing. Where it cannot be exact — a
            month already billed, or an arrears month still running — it says so above the lines
            rather than quietly answering a different question.
          </Prose>

          <ButtonRow>
            {clients.map((client) => (
              <Button
                key={client}
                size="sm"
                variant={client === chosen ? 'primary' : undefined}
                disabled={run.isPending}
                onClick={() => {
                  /* Clear the last client's preview before asking for this one's, or
                     the table below shows A's lines under B's name while B loads. */
                  run.reset()
                  setChosen(client)
                  run.mutate({ client, period, kind: 'monthly', dry_run: true })
                }}
              >
                {nameOf(client)}
              </Button>
            ))}
          </ButtonRow>

          {chosen === null ? (
            <Empty>Pick a client to see what their month comes to.</Empty>
          ) : run.isPending ? (
            <Empty>{`Running ${nameOf(chosen)}’s month and rolling it back…`}</Empty>
          ) : null}

          {review === null ? null : <NeedsReviewNotice review={review} />}
          {run.isError ? (
            <Notice need role="alert">
              {messageOf(run.error)}
            </Notice>
          ) : null}

          {preview === null ? null : <ScopeNotice preview={preview} />}

          {preview === null ? null : preview.would_issue || preview.already_issued !== null ? (
            <Table>
              <THead>
                <Tr>
                  <Th scope="col">Line</Th>
                  <Th scope="col" right>
                    Qty
                  </Th>
                  <Th scope="col" right>
                    Unit
                  </Th>
                  <Th scope="col" right>
                    Amount
                  </Th>
                </Tr>
              </THead>
              <TBody>
                {preview.lines.map((line) => (
                  <Tr key={line.seq}>
                    <Td>
                      {line.description}
                      {line.ref === null ? null : (
                        <Small>
                          <Muted>{` ${line.ref}`}</Muted>
                        </Small>
                      )}
                    </Td>
                    <Td right num>
                      {format.number(line.qty)}
                    </Td>
                    <Td right num>
                      <Num value={line.unit_amount} />
                    </Td>
                    <Td right num>
                      <Num value={line.amount} />
                    </Td>
                  </Tr>
                ))}
                <Tr variant="total">
                  <Td
                    colSpan={3}
                  >{`${nameOf(preview.client)} — ${format.monthLong(preview.period)}`}</Td>
                  <Td right num>
                    <B>
                      <Num value={preview.total} lg />
                    </B>
                  </Td>
                </Tr>
              </TBody>
            </Table>
          ) : (
            /* The edge case, said plainly. `would_issue: false` is a zero-line month:
               no invoice would be written at all, which is not a failure. */
            <Empty>
              {`Nothing to bill — ${nameOf(preview.client)} has no retainer line, no delivered extra and no billable usage for ${format.monthLong(preview.period)}.`}
            </Empty>
          )}
        </Stack>
      </Dialog>
    </>
  )
}

/**
 * Which of the three answers this is, said **above** the lines rather than left to be
 * inferred from them.
 *
 * The page still computes nothing: every word here is a field
 * (`already_issued`, `arrears_period`, `arrears_in_progress`) that `run.ts` `dryView`
 * decided, and the only thing this function does is choose a sentence.
 *
 * The already-billed strip is the ochre one. `CLAUDE.md` §Design: ochre is "this needs
 * your decision, or this costs you more", and a reader who thinks they are looking at
 * what August will bill when August was billed three weeks ago has a decision to take
 * — pick another month — before they read a single figure.
 *
 * The arrears sentence is on the exact answer too, and deliberately. "Extras and usage
 * are July, in arrears" is the single most surprising true thing about a monthly
 * invoice in this system, and a reader comparing the preview against the extras ledger
 * for the month on the seg needs it whether or not anything is wrong.
 */
function ScopeNotice({ preview }: { readonly preview: DryRunInvoiceView }) {
  const format = useFormat()
  const arrears = format.monthLong(preview.arrears_period)

  if (preview.already_issued !== null) {
    return (
      <Notice need role="status">
        {`${format.monthLong(preview.period)} has already been billed — ${preview.already_issued}. A run now writes nothing: one invoice per client per month is a database constraint, so the real run is a no-op. These are that invoice’s own lines and its own total, read back rather than computed again.`}
      </Notice>
    )
  }

  if (!preview.would_issue) return null

  return (
    <Notice role="status">
      {preview.arrears_in_progress
        ? `Extras and widget usage are ${arrears}, in arrears — and ${arrears} has not ended. Its usage is read as far as today and will be larger by the time the run happens. The retainer line is exact.`
        : `Exact, line for line. The retainer line is ${format.monthLong(preview.period)} in advance; the extras and the widget usage are ${arrears}, in arrears.`}
    </Notice>
  )
}

/**
 * The dry-run invoice off a `billing_run` answer, or null for anything else.
 *
 * `useStudioAction` types `data` as the action's own output **or** a `needs_review`
 * result, so the field is looked for rather than read. What sits under `dry_run` is
 * `DryRunInvoiceSchema`, which is what the assertion names — the same narrowing
 * `reviewOf` does for the other half of that union.
 */
function previewOf(data: unknown): DryRunInvoiceView | null {
  if (typeof data !== 'object' || data === null || !('dry_run' in data)) return null
  return (data as { readonly dry_run: DryRunInvoiceView | null }).dry_run
}
